import { isFunction } from '../../../utils/isFunction';

/**
 * Messages that trust form sends to us
 */
export type GooglePayFrameMessageIn =
    | {
          type: '@google-pay-trust/init-google-pay';
          payload: { environment: google.payments.api.Environment; merchantId: string; publicKey: string };
      }
    | { type: '@google-pay-trust/start'; payload: unknown }
    | { type: '@google-pay-trust/finish'; payload: { success: boolean } };

/**
 * Messages that we send to trust form
 */
export type GooglePayFrameMessageOut =
    | { type: '@google-pay-trust/init-google-pay-result'; payload: { ok: boolean } }
    | { type: '@google-pay-trust/cancelled'; payload: unknown }
    | {
          type: '@google-pay-trust/authorize-payment';
          payload: { token: google.payments.api.PaymentData };
      };

export class GooglePayProvider {
    private canMakePayments = false;
    private paymentsClient?: google.payments.api.PaymentsClient;

    private authorizePaymentCallback: null | ((success: boolean) => void) = null;

    /**
     * Creates Google Pay provider object, to supply trust form an access to Google Pay API on embedding page domain
     *
     * @param {Window} target Trust binding form frame window (for postMessage)
     * @param {string} targetOrigin Target page origin (for postMessage)
     * @param {google.payments.api.PaymentDataRequest} request Google Pay payment request
     */
    constructor(
        private target: Window,
        private targetOrigin: string,
        private request: google.payments.api.PaymentDataRequest,
    ) {
        this.listener = this.listener.bind(this);
    }

    /**
     * Start listening for requests from trust form
     */
    start() {
        window.addEventListener('message', this.listener);
    }

    /**
     * Stop listening for requests from trust form
     */
    stop() {
        window.removeEventListener('message', this.listener);

        this.cleanupSession();
    }

    private cleanupSession() {
        this.authorizePaymentCallback = null;
    }

    private initiateSession() {
        if (!this.canMakePayments || !this.paymentsClient) {
            throw new Error('No Google Pay support');
        }

        const onDone = () => {
            this.cleanupSession();
        };

        const onCancel = () => {
            this.target.postMessage(
                {
                    type: '@google-pay-trust/cancelled',
                    payload: {},
                } satisfies GooglePayFrameMessageOut,
                this.targetOrigin,
            );
        };

        this.paymentsClient.loadPaymentData(this.request).catch(onCancel).finally(onDone);
    }

    private listener(evt: MessageEvent) {
        if (evt.origin && evt.origin !== this.targetOrigin) {
            return;
        }

        const msg = evt.data as GooglePayFrameMessageIn;

        if (!msg.type || !msg.type.startsWith('@google-pay-trust')) {
            return;
        }

        switch (msg.type) {
            case '@google-pay-trust/init-google-pay':
                const { environment, merchantId, publicKey } = msg.payload;

                this.request = structuredClone(this.request);
                this.request.merchantInfo.merchantId = merchantId;
                for (const method of this.request.allowedPaymentMethods) {
                    if (method.type === 'CARD') {
                        method.tokenizationSpecification.parameters.publicKey = publicKey;
                    }
                }

                const onAuthorizePayment = (token: google.payments.api.PaymentData) => {
                    this.target.postMessage(
                        {
                            type: '@google-pay-trust/authorize-payment',
                            payload: { token },
                        } satisfies GooglePayFrameMessageOut,
                        this.targetOrigin,
                    );

                    return new Promise<google.payments.api.PaymentAuthorizationResult>((resolve) => {
                        this.authorizePaymentCallback = (success) => {
                            resolve({
                                transactionState: success ? 'SUCCESS' : 'ERROR',
                            });
                        };
                    });
                };

                if (window.google?.payments?.api?.PaymentsClient) {
                    this.paymentsClient = new google.payments.api.PaymentsClient({
                        environment,
                        paymentDataCallbacks: {
                            onPaymentAuthorized: onAuthorizePayment,
                        },
                    });

                    this.paymentsClient
                        .isReadyToPay({
                            apiVersion: this.request.apiVersion,
                            apiVersionMinor: this.request.apiVersionMinor,
                            allowedPaymentMethods: this.request.allowedPaymentMethods,
                        })
                        .then(({ result }) => {
                            this.canMakePayments = result;

                            this.target.postMessage(
                                {
                                    type: '@google-pay-trust/init-google-pay-result',
                                    payload: { ok: result },
                                } satisfies GooglePayFrameMessageOut,
                                this.targetOrigin,
                            );
                        })
                        .catch((reason) => {
                            console.error(`Can\'t get Google Pay availability: ${reason}`);

                            this.target.postMessage(
                                {
                                    type: '@google-pay-trust/init-google-pay-result',
                                    payload: { ok: false },
                                } satisfies GooglePayFrameMessageOut,
                                this.targetOrigin,
                            );
                        });
                } else {
                    this.target.postMessage(
                        {
                            type: '@google-pay-trust/init-google-pay-result',
                            payload: { ok: false },
                        } satisfies GooglePayFrameMessageOut,
                        this.targetOrigin,
                    );
                }

                break;

            case '@google-pay-trust/start':
                this.initiateSession();
                break;

            case '@google-pay-trust/finish':
                {
                    const { success } = msg.payload;

                    if (isFunction(this.authorizePaymentCallback)) {
                        this.authorizePaymentCallback(success);
                        this.authorizePaymentCallback = null;
                    }
                }
                break;
        }
    }
}
